import type { JSX } from 'react';
import { useState } from 'react';
import type { ReactNode } from 'react';

import { coverVisual, type CoverSource, type CoverVisual } from './cover-visual';
import './CourseCover.css';

export { coverVisual } from './cover-visual';
export type { CoverSource, CoverVisual } from './cover-visual';

/**
 * Обложки курсов (кит «LMS Система», HUB 2.0): подложка + брендовая иллюстрация, смайлик или своя
 * иконка из библиотеки, необязательная надпись. Выбор вида — целиком в cover-visual.ts
 * (`coverVisual`), здесь только отрисовка. Подложки — мягкие цвета-константы: плитка светлая и в
 * тёмной теме.
 */

/**
 * Рисунок обложки: смайлик (шрифт 'FK Emoji' — Noto Color Emoji, как в редакторе) или картинка.
 * Иконка из библиотеки могла быть удалена — тогда показываем запасную картинку, а не пустоту.
 * `size` — высота рисунка в px; без него размер и место задаёт CSS родителя (шапки курса).
 */
export function CoverArt({ v, size }: { v: CoverVisual; size?: number }): JSX.Element | null {
  const [failed, setFailed] = useState(false);
  if (v.kind === 'emoji') {
    return (
      <span className="fk-cover-emoji" aria-hidden style={size ? { fontSize: Math.round(size * 0.8), height: size } : undefined}>
        {v.emoji}
      </span>
    );
  }
  const src = failed ? v.fallbackSrc : v.src;
  if (v.kind === 'none' || !src) return null;
  return <img className="fk-cover-img" src={src} alt="" style={size ? { height: size } : undefined} onError={() => setFailed(true)} />;
}

/**
 * Плитка-обложка (каталог, мобильный список): рисунок справа по центру высоты; слева внизу —
 * надпись (жирная, цвет по контрасту к фону), под ней бейджи (`children`).
 */
export function CourseCover({ cover, height = 112, children }: { cover: CoverSource | string | null | undefined; height?: number; children?: ReactNode }): JSX.Element {
  const v = coverVisual(cover);
  return (
    <div className="fk-learn-ccover" style={{ height, background: v.bg }}>
      {/* key — смена иконки сбрасывает «не загрузилась» */}
      <span className="fk-cover-slot"><CoverArt key={v.src ?? v.emoji} v={v} size={Math.round(height * 0.82)} /></span>
      {(v.text || children) && (
        <div className="fk-cover-foot">
          {v.text && <b className="fk-cover-text" style={{ color: v.textColor }}>{v.text}</b>}
          {children && <div className="fk-cover-badges">{children}</div>}
        </div>
      )}
    </div>
  );
}

/** Надпись обложки в шапке («Продолжить», страница курса): внизу справа под рисунком; на телефоне скрыта. */
export function CoverHeroText({ v }: { v: CoverVisual }): JSX.Element | null {
  return v.text ? <span className="fk-cover-herotext">{v.text}</span> : null;
}

/**
 * Квадратная миниатюра обложки (список курсов конструктора, заголовок редактора, выбор в окне):
 * цвет фона + рисунок по центру, без надписи — на 28–44 px она нечитаема.
 */
export function CoverSquare({ cover, size = 38, rounded = true }: { cover: CoverSource | string | null | undefined; size?: number; rounded?: boolean }): JSX.Element {
  const v = coverVisual(cover);
  return (
    <span className="fk-cover-square" style={{ width: size, height: size, background: v.bg, borderRadius: rounded ? undefined : 0 }}>
      <CoverArt key={v.src ?? v.emoji} v={v} size={Math.round(size * 0.78)} />
    </span>
  );
}

/** Крупный образец обложки с надписью — живой предпросмотр в окне выбора. */
export function CoverPreview({ cover, width = 260, height = 130 }: { cover: CoverSource | string | null | undefined; width?: number; height?: number }): JSX.Element {
  return (
    <div style={{ width, maxWidth: '100%', borderRadius: 'var(--fk-radius-md)', overflow: 'hidden', border: '1px solid var(--fk-border)' }}>
      <CourseCover cover={cover} height={height} />
    </div>
  );
}
